/**
 * Demo Mode Context — fake patient data for clinic demonstrations.
 *
 * Run Q / Q-6 — DEV BUILDS ONLY. Recon A-05 found the banner told the truth
 * about one page: it says "Demo Mode — Sample data shown", but only the
 * Dashboard swaps in the constants below. Patients, Assessments, the wizard,
 * Front Desk and every report kept rendering real PHI underneath a banner
 * claiming they did not. Rather than fix a demo nobody asked for, the whole
 * thing is compiled out of a production build: `DEMO_MODE_AVAILABLE` is a
 * constant `import.meta.env.DEV`, so the bundler drops the toggle, the banner
 * and every branch behind it. A production load also CLEARS the stored key, so
 * a clinic that once switched it on is not left in a state nothing can exit.
 */
import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

/** Compile-time: demo mode exists in dev only. */
export const DEMO_MODE_AVAILABLE: boolean = import.meta.env.DEV;
export const DEMO_MODE_KEY = 'msk_demo_mode';

interface DemoContextType {
  isDemoMode: boolean;
  toggleDemoMode: () => void;
  setDemoMode: (on: boolean) => void;
}

const DemoContext = createContext<DemoContextType>({ isDemoMode: false, toggleDemoMode: () => {}, setDemoMode: () => {} });

export function DemoProvider({ children }: { children: ReactNode }): JSX.Element {
  const [isDemoMode, setIsDemoMode] = useState(
    () => DEMO_MODE_AVAILABLE && readDemoFlag(),
  );

  useEffect(() => {
    try {
      if (!DEMO_MODE_AVAILABLE) {
        // A production build removes the flag it can no longer honour.
        localStorage.removeItem(DEMO_MODE_KEY);
        return;
      }
      localStorage.setItem(DEMO_MODE_KEY, isDemoMode ? 'true' : 'false');
    } catch {
      // private browsing / storage disabled — demo mode is a convenience
    }
  }, [isDemoMode]);

  return (
    <DemoContext.Provider value={{
      isDemoMode: DEMO_MODE_AVAILABLE && isDemoMode,
      toggleDemoMode: () => { if (DEMO_MODE_AVAILABLE) setIsDemoMode(v => !v); },
      setDemoMode: (on: boolean) => { if (DEMO_MODE_AVAILABLE) setIsDemoMode(on); },
    }}>
      {children}
    </DemoContext.Provider>
  );
}

function readDemoFlag(): boolean {
  try { return localStorage.getItem(DEMO_MODE_KEY) === 'true'; } catch { return false; }
}

export const useDemoMode = () => useContext(DemoContext);

// ── Fake demo data ──

export const DEMO_PATIENTS = [
  { id: 'demo-1', firstName: 'Maria', lastName: 'Georgiou', dateOfBirth: '1984-03-15', gender: 'female', email: 'maria.g@email.com', phone: '+357 99 123456', carePhase: 'active', mrn: 'MSK-DEMO-001' },
  { id: 'demo-2', firstName: 'Andreas', lastName: 'Konstantinou', dateOfBirth: '1971-08-22', gender: 'male', email: 'andreas.k@email.com', phone: '+357 99 234567', carePhase: 'maintenance', mrn: 'MSK-DEMO-002' },
  { id: 'demo-3', firstName: 'Elena', lastName: 'Papadopoulos', dateOfBirth: '1998-01-10', gender: 'female', email: 'elena.p@email.com', phone: '+357 99 345678', carePhase: 'initial', mrn: 'MSK-DEMO-003' },
  { id: 'demo-4', firstName: 'Christos', lastName: 'Ioannou', dateOfBirth: '1959-11-03', gender: 'male', email: 'christos.i@email.com', phone: '+357 99 456789', carePhase: 'active', mrn: 'MSK-DEMO-004' },
  { id: 'demo-5', firstName: 'Sofia', lastName: 'Nicolaou', dateOfBirth: '1991-06-28', gender: 'female', email: 'sofia.n@email.com', phone: '+357 99 567890', carePhase: 'wellness', mrn: 'MSK-DEMO-005' },
  { id: 'demo-6', firstName: 'Dimitris', lastName: 'Charalambous', dateOfBirth: '1978-04-17', gender: 'male', email: 'dimitris.c@email.com', phone: '+357 99 678901', carePhase: 'initial', mrn: 'MSK-DEMO-006' },
  { id: 'demo-7', firstName: 'Katerina', lastName: 'Michaelidou', dateOfBirth: '1965-09-05', gender: 'female', email: 'katerina.m@email.com', phone: '+357 99 789012', carePhase: 'active', mrn: 'MSK-DEMO-007' },
  { id: 'demo-8', firstName: 'Panayiotis', lastName: 'Stavrou', dateOfBirth: '1988-12-20', gender: 'male', email: 'panayiotis.s@email.com', phone: '+357 99 890123', carePhase: 'maintenance', mrn: 'MSK-DEMO-008' },
  { id: 'demo-9', firstName: 'Anna', lastName: 'Petridou', dateOfBirth: '2001-02-14', gender: 'female', email: 'anna.p@email.com', phone: '+357 99 901234', carePhase: 'initial', mrn: 'MSK-DEMO-009' },
  { id: 'demo-10', firstName: 'George', lastName: 'Loizou', dateOfBirth: '1973-07-09', gender: 'male', email: 'george.l@email.com', phone: '+357 99 012345', carePhase: 'active', mrn: 'MSK-DEMO-010' },
  { id: 'demo-11', firstName: 'Eleni', lastName: 'Andreou', dateOfBirth: '1955-05-30', gender: 'female', email: 'eleni.a@email.com', phone: '+357 99 111222', carePhase: 'wellness', mrn: 'MSK-DEMO-011' },
  { id: 'demo-12', firstName: 'Nikos', lastName: 'Demetriou', dateOfBirth: '1982-10-12', gender: 'male', email: 'nikos.d@email.com', phone: '+357 99 222333', carePhase: 'active', mrn: 'MSK-DEMO-012' },
];

export const DEMO_ASSESSMENTS = [
  { id: 'demo-a1', patientId: 'demo-1', patientName: 'Maria Georgiou', type: 'full_dx', status: 'completed', createdAt: Date.now() - 7 * 86400000 },
  { id: 'demo-a2', patientId: 'demo-2', patientName: 'Andreas Konstantinou', type: 'full_dx', status: 'completed', createdAt: Date.now() - 3 * 86400000 },
  { id: 'demo-a3', patientId: 'demo-3', patientName: 'Elena Papadopoulos', type: 'full_dx', status: 'draft', createdAt: Date.now() - 1 * 86400000 },
  { id: 'demo-a4', patientId: 'demo-4', patientName: 'Christos Ioannou', type: 're_exam', status: 'in_progress', createdAt: Date.now() },
];

export const DEMO_APPOINTMENTS = [
  { starts_at: new Date().setHours(9, 0), patient_name: 'Maria Georgiou', appointment_type_name: 'Follow-up', status: 'arrived' },
  { starts_at: new Date().setHours(9, 30), patient_name: 'Elena Papadopoulos', appointment_type_name: 'Initial Assessment', status: 'scheduled' },
  { starts_at: new Date().setHours(10, 15), patient_name: 'Christos Ioannou', appointment_type_name: 'Re-examination', status: 'scheduled' },
  { starts_at: new Date().setHours(11, 0), patient_name: 'Sofia Nicolaou', appointment_type_name: 'Maintenance', status: 'scheduled' },
  { starts_at: new Date().setHours(14, 0), patient_name: 'Dimitris Charalambous', appointment_type_name: 'Initial Assessment', status: 'scheduled' },
  { starts_at: new Date().setHours(15, 30), patient_name: 'George Loizou', appointment_type_name: 'Follow-up', status: 'scheduled' },
];

export const DEMO_STATS = {
  totalPatients: 12,
  activePatients: 8,
  assessmentsToday: 3,
  assessmentsThisMonth: 18,
  assessmentsTotal: 67,
  pendingDdxReviews: 2,
  avgWellnessScore: 78,
  completionRate: 85,
  bodyRegions: [
    { region: 'Lumbar', count: 24 },
    { region: 'Cervical', count: 19 },
    { region: 'Shoulder', count: 14 },
    { region: 'Knee', count: 11 },
    { region: 'Hip', count: 8 },
  ],
  monthlyTrend: [
    { month: 'Jan', count: 12 }, { month: 'Feb', count: 15 },
    { month: 'Mar', count: 18 }, { month: 'Apr', count: 14 },
  ],
};
